import React, { useState } from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Help as HelpIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  ExpandMore,
} from "@mui/icons-material";
import DraggableHelpDialog from "../../common/components/DraggableHelpDialog";

const TimezoneHelp = ({ open, onClose }) => {
  const [expandedPanel, setExpandedPanel] = useState("overview");

  const handlePanelChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  return (
    <DraggableHelpDialog dgKey="timezone-help" open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HelpIcon color="primary" />
          <Typography variant="h6">Timezone Help Guide</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box>
          {/* Overview */}
          <Accordion
            expanded={expandedPanel === "overview"}
            onChange={handlePanelChange("overview")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">System Overview</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography paragraph>
                The Timezone Maintenance tab allows you to manage the list of
                timezones available in the application. Timezones ensure that
                dates and times are displayed correctly based on user location.
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Basic Operations & Field Information */}
          <Accordion
            expanded={expandedPanel === "basicOperations"}
            onChange={handlePanelChange("basicOperations")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Basic Operations & Field Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Basic Operations */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: "100%" }}>
                    <Typography variant="h6" gutterBottom>
                      Basic Operations
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <AddIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Add Timezone"
                          secondary="Click the + button to create a new timezone entry"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <EditIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Edit Timezone"
                          secondary="Click the edit icon in the actions column to modify an existing timezone"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <SearchIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Search"
                          secondary="Use the search bar to find timezones by name or offset"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <FilterIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Filter Timezones"
                          secondary="Filter the list by region, offset, or usage status"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <DeleteIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Delete Timezone"
                          secondary="Click the delete icon to remove a timezone entry"
                        />
                      </ListItem>
                    </List>
                  </Paper>
                </Grid>

                {/* Field Information */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: "100%" }}>
                    <Typography variant="h6" gutterBottom>
                      Field Requirements
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Required Fields"
                          secondary="Timezone Name and IANA are required for all records"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <InfoIcon color="info" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Unique Name"
                          secondary="Each timezone name must be unique to avoid conflicts"
                        />
                      </ListItem>
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Column Descriptions */}
          <Accordion
            expanded={expandedPanel === "columnDescriptions"}
            onChange={handlePanelChange("columnDescriptions")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Column Descriptions</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" component="div">
                <strong>Timezone Name:</strong> Official timezone identifier (e.g., America/New_York)
                <br />
                <strong>Offset:</strong> Time difference from UTC (e.g., UTC-5)
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Tips & Best Practices*/}
          <Accordion
            expanded={expandedPanel === "tips"}
            onChange={handlePanelChange("tips")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Tips & Best Practices</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" component="div">
                • Use standard IANA timezone names for compatibility
                <br />
                • Avoid creating duplicate timezones with the same offset
                <br />
                • Keep inactive timezones hidden to simplify selection
                <br />
                • Always verify offsets during Daylight Saving Time changes
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </DraggableHelpDialog>
  );
};

export default TimezoneHelp;
